import React from "react";
import { Navigate } from "react-router-dom";
import { LOGIN_PATH, useAuth } from "@/contexts/AuthContext";
import { permissionService } from "@/services/permissionService";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  featureFlag?: string;
  redirectUnauthenticated?: boolean;
  deniedRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  featureFlag,
  redirectUnauthenticated = false,
  deniedRoles,
}) => {
  const {
    user,
    selectedContext,
    activeRole,
    hasPermission,
    isInitializing,
    isSelectingContext,
  } = useAuth();

  // console.log('🛡️ ProtectedRoute render:', {
  //   path: window.location.pathname,
  //   hasUser: !!user,
  //   selectedContext,
  //   activeRole,
  //   isInitializing,
  //   isSelectingContext,
  //   requiredPermission
  // });

  // Step 1: Show loading ONLY during actual initialization
  if (isInitializing) {
    console.log("⏳ ProtectedRoute: Initializing authentication...");
    return null;
  }

  // Step 2: If no user, redirect to login immediately
  if (!user && !isSelectingContext) {
    if (redirectUnauthenticated) {
      return <Navigate to={LOGIN_PATH} replace />;
    }
    return null;
  }

  if (!user) {
    return null;
  }

  // Step 3: If user exists but no active role, show loading (waiting for role selection)
  if (user && !activeRole && !isSelectingContext) {
    console.log("⏳ ProtectedRoute: User authenticated, waiting for role selection...");
    return null;
  }

  // Do not mount protected pages until the selected context and active role
  // are final; child effects could otherwise issue requests with stale access.
  if (isSelectingContext) {
    console.log("🔄 ProtectedRoute: Context selection in progress");
    return null;
  }

  // Context selection screen removed — the landing context is resolved from
  // account types during the Auth0 callback.
  // if (
  //   user.contexts &&
  //   user.contexts.length > 1 &&
  //   !selectedContext
  // ) {
  //   return <Navigate to="/context-selection" replace />;
  // }

  // If feature flag required, check it
  if (featureFlag && !permissionService.isFeatureEnabled(featureFlag)) {
    return <Navigate to="/dashboard" replace />;
  }

  // If permission required, check it
  if (requiredPermission) {
    const hasRequiredPermission = hasPermission(requiredPermission);
    console.log("🔒 ProtectedRoute: Permission check", {
      required: requiredPermission,
      hasPermission: hasRequiredPermission,
      activeRole: activeRole, // activeRole is a string like 'OWNER', not an object
      selectedContext,
      user: user?.name,
    });

    if (!hasRequiredPermission) {
      console.warn(
        "❌ ProtectedRoute: Permission denied, redirecting to /unauthorized",
      );
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Explicit role denylist — enforced client-side even if the backend
  // permissions API incorrectly grants a permission to this role.
  if (deniedRoles && activeRole && deniedRoles.includes(activeRole)) {
    console.warn(
      "❌ ProtectedRoute: Role denied, redirecting to /unauthorized",
      { activeRole, deniedRoles },
    );
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};
